import * as THREE from 'three'
import { TileType } from '../../data/maze-data'
import { gridToWorld } from '../../utils/grid'
import { materials } from '../../procedural/materials'
import { buildShell, buildKrabbyPatty } from '../../procedural/models'
import type { GridPos } from '../../types'
import type { Maze } from '../world/Maze'

export type CollectKind = 'pearl' | 'shell' | 'power'
export interface CollectResult {
  kind: CollectKind
  x: number
  z: number
}

const key = (r: number, c: number) => `${r},${c}`

/** Pearls (instanced) + a few authored shell bonuses + Krabby Patty power-ups. */
export class Collectibles {
  readonly group = new THREE.Group()
  remaining = 0 // pearls + shells needed to clear the level

  private pearlMesh: THREE.InstancedMesh
  private pearlGlowMaterial: THREE.ShaderMaterial
  private pearlBase: Array<{ x: number; z: number }> = []
  private pearlActive: boolean[] = []
  private cellToPearl = new Map<string, number>()
  private dummy = new THREE.Object3D()

  private shells = new Map<string, { group: THREE.Group; active: boolean }>()
  private patties = new Map<string, { group: THREE.Group; active: boolean }>()
  private t = 0
  private readonly pearlY = 0.18

  constructor(maze: Maze, environment: THREE.Texture | null = null) {
    const m = materials()
    // Explicit maps retain the pearl's specular intensity. With scene.environment
    // alone Three uses the scene's low diffuse intensity for this material too.
    m.pearl.envMap = environment
    const tiles = maze.pelletTiles()
    const dotTiles = tiles.filter((t) => t.type === TileType.Dot)
    const powerTiles = tiles.filter((t) => t.type === TileType.PowerPellet)

    // Choose ~6 evenly-spread dots to become authored shell bonuses.
    const shellIdx = new Set<number>()
    for (let i = 0; i < 6 && dotTiles.length > 6; i++) {
      shellIdx.add(Math.floor(((i + 0.5) / 6) * dotTiles.length))
    }

    const pearlTiles = dotTiles.filter((_, i) => !shellIdx.has(i))
    const geo = new THREE.IcosahedronGeometry(0.108, 2)
    this.pearlMesh = new THREE.InstancedMesh(geo, m.pearl, Math.max(1, pearlTiles.length))
    this.pearlMesh.frustumCulled = false
    pearlTiles.forEach((t, i) => {
      const w = gridToWorld(t.col, t.row)
      this.pearlBase[i] = { x: w.x, z: w.z }
      this.pearlActive[i] = true
      this.cellToPearl.set(key(t.row, t.col), i)
      this.dummy.position.set(w.x, this.pearlY, w.z)
      this.dummy.scale.setScalar(1)
      this.dummy.updateMatrix()
      this.pearlMesh.setMatrixAt(i, this.dummy.matrix)
    })
    this.pearlMesh.instanceMatrix.needsUpdate = true
    this.group.add(this.pearlMesh)
    this.pearlGlowMaterial = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      vertexShader: `varying vec2 vPearlUv;
        void main() {
          vPearlUv = uv;
          vec4 center = modelViewMatrix * instanceMatrix * vec4(0.0,0.0,0.0,1.0);
          center.xy += position.xy * length(instanceMatrix[0].xyz);
          gl_Position = projectionMatrix * center;
        }`,
      fragmentShader: `varying vec2 vPearlUv;
        void main() {
          float r = length(vPearlUv - 0.5) * 2.0;
          float halo = exp(-r * r * 4.5) * (1.0 - smoothstep(0.55, 1.0, r));
          gl_FragColor = vec4(0.24,0.72,1.0,halo * 0.22);
        }`,
    })
    const glow = new THREE.InstancedMesh(new THREE.PlaneGeometry(0.45, 0.45), this.pearlGlowMaterial, this.pearlMesh.count)
    // One shared transform buffer ensures collection hides sphere and halo atomically.
    glow.instanceMatrix = this.pearlMesh.instanceMatrix
    glow.frustumCulled = false
    this.group.add(glow)

    dotTiles.forEach((t, i) => {
      if (!shellIdx.has(i)) return
      const g = buildShell()
      const w = gridToWorld(t.col, t.row)
      g.position.set(w.x, 0.18, w.z)
      this.shells.set(key(t.row, t.col), { group: g, active: true })
      this.group.add(g)
    })

    powerTiles.forEach((t) => {
      const g = buildKrabbyPatty()
      const w = gridToWorld(t.col, t.row)
      g.position.set(w.x, 0.24, w.z)
      this.patties.set(key(t.row, t.col), { group: g, active: true })
      this.group.add(g)
    })

    this.remaining = pearlTiles.length + this.shells.size
  }

  tryCollect(cell: GridPos): CollectResult | null {
    const k = key(cell.row, cell.col)
    const pi = this.cellToPearl.get(k)
    if (pi !== undefined && this.pearlActive[pi]) {
      this.pearlActive[pi] = false
      this.dummy.position.set(this.pearlBase[pi].x, this.pearlY, this.pearlBase[pi].z)
      this.dummy.scale.setScalar(0)
      this.dummy.updateMatrix()
      this.pearlMesh.setMatrixAt(pi, this.dummy.matrix)
      this.pearlMesh.instanceMatrix.needsUpdate = true
      this.remaining--
      return { kind: 'pearl', x: this.pearlBase[pi].x, z: this.pearlBase[pi].z }
    }
    const sh = this.shells.get(k)
    if (sh && sh.active) {
      sh.active = false
      sh.group.visible = false
      this.remaining--
      return { kind: 'shell', x: sh.group.position.x, z: sh.group.position.z }
    }
    const pa = this.patties.get(k)
    if (pa && pa.active) {
      pa.active = false
      pa.group.visible = false
      return { kind: 'power', x: pa.group.position.x, z: pa.group.position.z }
    }
    return null
  }

  update(dt: number): void {
    this.t += dt
    // Pearls are static (set once at build / on collect) — animating 230 instance
    // matrices every frame is a needless per-frame GPU upload. Only the handful of
    // authored shells + Krabby Patties animate.
    for (const sh of this.shells.values()) {
      if (sh.active) sh.group.rotation.y = this.t * 1.2
    }
    const pulse = 1 + Math.sin(this.t * 4) * 0.12
    for (const pa of this.patties.values()) {
      if (!pa.active) continue
      pa.group.rotation.y = this.t * 0.8
      pa.group.scale.setScalar(pulse)
      pa.group.position.y = 0.24 + Math.sin(this.t * 3) * 0.06
    }
  }

  dispose(): void {
    this.pearlGlowMaterial.dispose()
    // Free every owned geometry (pearl InstancedMesh + all shell/patty group meshes).
    // Materials are shared singletons from materials() — do NOT dispose them.
    this.group.traverse((obj) => {
      const mesh = obj as THREE.Mesh
      if (mesh.geometry) mesh.geometry.dispose()
    })
  }
}
